import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import { useHighlightedStores } from '../../context/HighlightedStoresContext';
import api from '../../services/api';
import AdminStoreSearchCombobox from './AdminStoreSearchCombobox';

function formatCell(val, col, t) {
  if (col === 'acceptStatus') {
    if (val === true) return t('wbAdminReturns.acceptYes');
    if (val === false) return t('wbAdminReturns.acceptNo');
    return '—';
  }
  if (col === 'returnsSource') {
    if (val === 'internal') return t('wbAdminReturns.returnsSourceInternal');
    if (val === 'foreign') return t('wbAdminReturns.returnsSourceForeign');
    return val == null || val === '' ? '' : String(val);
  }
  if (val == null || val === '') return '';
  if (typeof val === 'object') {
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }
  return String(val);
}

/** Columns shown only after clicking a row (detail strip). */
const DETAIL_ONLY_COLUMNS = new Set([
  'snapshotId',
  'storeDbId',
  'storeName',
  'brand',
  'subjectName',
  'techSize',
  'dstOfficeId',
]);

const WB_COLUMN_T_KEYS = {
  snapshotId: 'wbAdminReturns.colSnapshotId',
  storeSellerId: 'wbAdminReturns.colStoreSellerId',
  storeDbId: 'wbAdminReturns.colStoreDbId',
  storeName: 'wbAdminReturns.colStoreName',
  srid: 'wbAdminReturns.colSrid',
  nmId: 'wbAdminReturns.colNmId',
  barcode: 'wbAdminReturns.colBarcode',
  brand: 'wbAdminReturns.colBrand',
  subjectName: 'wbAdminReturns.colSubjectName',
  techSize: 'wbAdminReturns.colTechSize',
  returnType: 'wbAdminReturns.colReturnType',
  reason: 'wbAdminReturns.colReason',
  status: 'wbAdminReturns.colStatus',
  acceptStatus: 'wbAdminReturns.colAcceptStatus',
  returnsSource: 'wbAdminReturns.colReturnsSource',
  orderDt: 'wbAdminReturns.colOrderDt',
  orderId: 'wbAdminReturns.colOrderId',
  completedDt: 'wbAdminReturns.colCompletedDt',
  readyToReturnDt: 'wbAdminReturns.colReadyToReturnDt',
  expiredDt: 'wbAdminReturns.colExpiredDt',
  dstOfficeAddress: 'wbAdminReturns.colDstOfficeAddress',
  dstOfficeId: 'wbAdminReturns.colDstOfficeId',
  isStatusActive: 'wbAdminReturns.colIsStatusActive',
  shkId: 'wbAdminReturns.colShkId',
  stickerId: 'wbAdminReturns.colStickerId',
};

function wbColumnHeader(t, col) {
  const path = WB_COLUMN_T_KEYS[col];
  if (path) {
    const label = t(path);
    if (label && !label.startsWith('wbAdminReturns.')) return label;
  }
  return col;
}

const AdminWBStoreReturnsPage = () => {
  const { t } = useLanguage();
  const { isSellerIdHighlighted } = useHighlightedStores();

  const [storeId, setStoreId] = useState('');
  const [selectedSellerId, setSelectedSellerId] = useState('');

  const [pvzScope, setPvzScope] = useState('');
  const [wbDtFrom, setWbDtFrom] = useState('');
  const [wbDtTo, setWbDtTo] = useState('');
  const [wbFieldCompleted, setWbFieldCompleted] = useState(false);
  const [wbFieldReady, setWbFieldReady] = useState(false);
  const [wbFieldExpired, setWbFieldExpired] = useState(false);
  const [wbFieldOrderDt, setWbFieldOrderDt] = useState(false);

  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(500);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});

  const [searchText, setSearchText] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [filterAcceptStatus, setFilterAcceptStatus] = useState('');
  const [filterReturnsSource, setFilterReturnsSource] = useState('');

  const [exporting, setExporting] = useState(false);
  const [fetchedOnce, setFetchedOnce] = useState(false);
  const [creatingSnapshotId, setCreatingSnapshotId] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);

  const skipStoreReload = useRef(true);

  const buildQueryParams = useCallback(
    (pageNum) => {
      const params = { page: pageNum, page_size: pageSize };
      if (storeId) params.store = storeId;
      if (searchText.trim()) params.q = searchText.trim();
      if (filterActive === '0' || filterActive === '1') params.is_status_active = filterActive;
      if (filterAcceptStatus === 'true' || filterAcceptStatus === 'false') {
        params.accept_status = filterAcceptStatus;
      }
      if (filterReturnsSource === 'foreign' || filterReturnsSource === 'internal') {
        params.returns_source = filterReturnsSource;
      }
      if (pvzScope.trim()) params.pvz_scope = pvzScope.trim();
      const wbFields = [];
      if (wbFieldCompleted) wbFields.push('completed_dt');
      if (wbFieldReady) wbFields.push('ready_to_return_dt');
      if (wbFieldExpired) wbFields.push('expired_dt');
      if (wbFieldOrderDt) wbFields.push('order_dt');
      if (wbDtFrom.trim() && wbDtTo.trim() && wbFields.length) {
        params.wb_dt_from = wbDtFrom.trim();
        params.wb_dt_to = wbDtTo.trim();
        params.wb_dt_fields = wbFields.join(',');
      }
      return params;
    },
    [
      pageSize,
      storeId,
      searchText,
      filterActive,
      filterAcceptStatus,
      filterReturnsSource,
      pvzScope,
      wbDtFrom,
      wbDtTo,
      wbFieldCompleted,
      wbFieldReady,
      wbFieldExpired,
      wbFieldOrderDt,
    ],
  );

  const fetchPage = useCallback(
    async (pageNum) => {
      setPage(pageNum);
      setExpandedRowId(null);
      setLoading(true);
      setError('');
      const params = buildQueryParams(pageNum);
      try {
        const payload = await api.getAdminWBStoreReturns(params);
        if (payload?.error) {
          setError(payload.error);
          setColumns(payload.columns || []);
          setRows([]);
          setMeta(payload.meta || {});
          setTotalCount(0);
          return;
        }
        setColumns(Array.isArray(payload?.columns) ? payload.columns : []);
        setRows(Array.isArray(payload?.rows) ? payload.rows : []);
        const m = payload?.meta && typeof payload.meta === 'object' ? payload.meta : {};
        setMeta(m);
        setTotalCount(Number(m.total_count) || 0);
        if (m.page_size) setPageSize(Number(m.page_size));
      } catch (err) {
        setError(err.message || t('wbAdminReturns.loadError'));
        setColumns([]);
        setRows([]);
        setMeta({});
        setTotalCount(0);
      } finally {
        setLoading(false);
        setFetchedOnce(true);
      }
    },
    [buildQueryParams, t],
  );

  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  useEffect(() => {
    fetchPageRef.current(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  useEffect(() => {
    if (skipStoreReload.current) {
      skipStoreReload.current = false;
      return;
    }
    fetchPageRef.current(1);
  }, [storeId]);

  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / (pageSize || 500)));

  const visibleColumns = useMemo(
    () => columns.filter((c) => !DETAIL_ONLY_COLUMNS.has(c)),
    [columns],
  );

  const detailColumns = useMemo(
    () => columns.filter((c) => DETAIL_ONLY_COLUMNS.has(c)),
    [columns],
  );

  const getExportParams = () => {
    const p = buildQueryParams(1);
    const { page: _pg, page_size: _ps, ...rest } = p;
    return rest;
  };

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const res = await api.downloadAdminWBStoreReturnsExcel(getExportParams());
      const blob = res?.data;
      if (!blob) throw new Error(t('wbAdminReturns.loadError'));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      const part = storeId ? `store_${storeId}` : 'all';
      a.download = `wb_returns_snapshot_${part}_${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      setError(err.message || t('wbAdminReturns.exportError'));
    } finally {
      setExporting(false);
    }
  };

  const handleAddToOrder = async (snapshotId) => {
    if (snapshotId == null) return;
    setCreatingSnapshotId(snapshotId);
    setError('');
    try {
      await api.postAdminWBStoreReturnsCreateOrder(snapshotId);
    } catch (err) {
      setError(err.message || t('wbAdminReturns.addToOrderError'));
    } finally {
      setCreatingSnapshotId(null);
    }
  };

  const wbTableRowHighlight =
    selectedSellerId && isSellerIdHighlighted(selectedSellerId);

  const storeErrors = meta?.store_errors && typeof meta.store_errors === 'object' ? meta.store_errors : {};
  const storeErrKeys = Object.keys(storeErrors);

  const lastSynced = meta?.snapshot_last_sync_at;

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchPage(1);
  };

  return (
    <div className="admin-wb-returns-page">
      <h4 className="mb-2 fw-bold">{t('wbAdminReturns.title')}</h4>
      <p className="text-muted small mb-4">{t('wbAdminReturns.intro')}</p>

      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={handleFilterSubmit}>
            <Row className="g-3 align-items-end mb-2">
              <Col md={6} lg={5}>
                <Form.Label>{t('wbAdminReturns.storeOptional')}</Form.Label>
                <AdminStoreSearchCombobox
                  className="admin-wb-store-combobox"
                  filterValue={storeId}
                  onFilterValueChange={(pk) => {
                    setStoreId(pk);
                    if (!pk) setSelectedSellerId('');
                  }}
                  onStoreSelected={(s) => {
                    setSelectedSellerId(
                      s && s.store_id != null ? String(s.store_id) : '',
                    );
                  }}
                  valueField="id"
                  searchExtras={{ store_type: 'wildberries', has_api_key: true }}
                  limit={50}
                  placeholder={t('wbAdminReturns.storeSearchPlaceholder')}
                  loadingText={t('wbAdminReturns.storeSearching')}
                  noMatchesText={t('wbAdminReturns.storeNoMatches')}
                />
                <Form.Text className="text-muted">{t('wbAdminReturns.storeOptionalHint')}</Form.Text>
              </Col>
              <Col md={2}>
                <Form.Label className="small text-muted mb-0">{t('wbAdminReturns.acceptStatusFilter')}</Form.Label>
                <Form.Select
                  value={filterAcceptStatus}
                  onChange={(e) => setFilterAcceptStatus(e.target.value)}
                >
                  <option value="">{t('wbAdminReturns.acceptStatusAll')}</option>
                  <option value="true">{t('wbAdminReturns.acceptYes')}</option>
                  <option value="false">{t('wbAdminReturns.acceptNo')}</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="small text-muted mb-0">{t('wbAdminReturns.returnsSourceFilter')}</Form.Label>
                <Form.Select
                  value={filterReturnsSource}
                  onChange={(e) => setFilterReturnsSource(e.target.value)}
                >
                  <option value="">{t('wbAdminReturns.returnsSourceAll')}</option>
                  <option value="foreign">{t('wbAdminReturns.returnsSourceForeign')}</option>
                  <option value="internal">{t('wbAdminReturns.returnsSourceInternal')}</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="small text-muted mb-0">{t('wbAdminReturns.isStatusActive')}</Form.Label>
                <Form.Select
                  value={filterActive}
                  onChange={(e) => setFilterActive(e.target.value)}
                >
                  <option value="">{t('wbAdminReturns.anyActive')}</option>
                  <option value="1">1</option>
                  <option value="0">0</option>
                </Form.Select>
              </Col>
              <Col md="auto" className="d-flex flex-wrap gap-2">
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('wbAdminReturns.loading')}
                    </>
                  ) : (
                    t('wbAdminReturns.applyFilters')
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline-success"
                  onClick={handleExport}
                  disabled={exporting}
                >
                  {exporting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('wbAdminReturns.exporting')}
                    </>
                  ) : (
                    t('wbAdminReturns.downloadExcel')
                  )}
                </Button>
              </Col>
            </Row>
            <Row className="g-3 align-items-end">
              <Col xs={12} md={8}>
                <Form.Label className="small text-muted mb-0">{t('wbAdminReturns.search')}</Form.Label>
                <Form.Control
                  type="search"
                  placeholder={t('wbAdminReturns.searchPlaceholder')}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <Form.Text className="text-muted">{t('wbAdminReturns.searchHint')}</Form.Text>
              </Col>
            </Row>
            <Row className="g-3 align-items-end mt-2">
              <Col xs={12} md={3}>
                <Form.Label className="small text-muted mb-0">{t('wbAdminReturns.pvzScope')}</Form.Label>
                <Form.Select value={pvzScope} onChange={(e) => setPvzScope(e.target.value)}>
                  <option value="">{t('wbAdminReturns.pvzScopeAll')}</option>
                  <option value="shosseynaya">{t('wbAdminReturns.pvzShosseynaya')}</option>
                  <option value="proezd_3723">{t('wbAdminReturns.pvzProezd3723')}</option>
                  <option value="warehouse">{t('wbAdminReturns.pvzOurWarehouse')}</option>
                  <option value="our_pvz">{t('wbAdminReturns.pvzOurPvz')}</option>
                  <option value="other">{t('wbAdminReturns.pvzOther')}</option>
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="small text-muted mb-0">{t('wbAdminReturns.wbDtFrom')}</Form.Label>
                <Form.Control type="date" value={wbDtFrom} onChange={(e) => setWbDtFrom(e.target.value)} />
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="small text-muted mb-0">{t('wbAdminReturns.wbDtTo')}</Form.Label>
                <Form.Control type="date" value={wbDtTo} onChange={(e) => setWbDtTo(e.target.value)} />
              </Col>
              <Col xs={12} md={5}>
                <Form.Label className="small text-muted mb-0">{t('wbAdminReturns.wbDtFieldsLabel')}</Form.Label>
                <div className="d-flex flex-wrap gap-3 small">
                  <Form.Check
                    id="wb-dt-completed"
                    label={t('wbAdminReturns.wbFieldCompletedDt')}
                    checked={wbFieldCompleted}
                    onChange={(e) => setWbFieldCompleted(e.target.checked)}
                  />
                  <Form.Check
                    id="wb-dt-ready"
                    label={t('wbAdminReturns.wbFieldReadyToReturnDt')}
                    checked={wbFieldReady}
                    onChange={(e) => setWbFieldReady(e.target.checked)}
                  />
                  <Form.Check
                    id="wb-dt-expired"
                    label={t('wbAdminReturns.wbFieldExpiredDt')}
                    checked={wbFieldExpired}
                    onChange={(e) => setWbFieldExpired(e.target.checked)}
                  />
                  <Form.Check
                    id="wb-dt-order"
                    label={t('wbAdminReturns.wbFieldOrderDt')}
                    checked={wbFieldOrderDt}
                    onChange={(e) => setWbFieldOrderDt(e.target.checked)}
                  />
                </div>
                <Form.Text className="text-muted">{t('wbAdminReturns.wbDtHint')}</Form.Text>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      {lastSynced ? (
        <p className="small text-muted mb-2">
          {t('wbAdminReturns.lastSynced')}: {formatCell(lastSynced, null, t)}
        </p>
      ) : null}

      {storeErrKeys.length > 0 && (
        <Alert variant="warning" className="small">
          <div className="fw-semibold mb-1">{t('wbAdminReturns.partialStoreErrors')}</div>
          <ul className="mb-0 ps-3">
            {storeErrKeys.map((pk) => (
              <li key={pk}>
                {t('wbAdminReturns.storePk')} {pk}: {storeErrors[pk]}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {(rows.length > 0 || totalCount > 0) && (
        <>
          <p className="small text-muted mb-2">
            {t('wbAdminReturns.records')}: {rows.length}
            {totalCount > 0 ? (
              <span className="ms-2">
                · {t('billing.shownOf', {
                  from: (page - 1) * pageSize + 1,
                  to: Math.min(page * pageSize, totalCount),
                  total: totalCount,
                })}
              </span>
            ) : null}
            {meta?.stores_queried != null && meta.mode === 'all' ? (
              <span className="ms-2">
                · {t('wbAdminReturns.storesInResult')}: {meta.stores_queried}
              </span>
            ) : null}
          </p>
          <Row className="g-2 mb-2 align-items-center">
            <Col md="auto" className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={loading || page <= 1}
                onClick={() => fetchPage(page - 1)}
              >
                {t('wbAdminReturns.pagePrev')}
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={loading || page >= totalPages}
                onClick={() => fetchPage(page + 1)}
              >
                {t('wbAdminReturns.pageNext')}
              </Button>
              <span className="small text-muted align-self-center">
                {t('billing.pageOf', { page, total: totalPages })}
              </span>
            </Col>
          </Row>

          {rows.length > 0 && (
            <div className="admin-wb-returns-table-scroll">
              <Table
                striped
                bordered
                hover
                size="sm"
                className="small admin-wb-returns-data-table mb-0"
              >
                <thead className="table-light sticky-top" style={{ zIndex: 2 }}>
                  <tr>
                    <th className="text-nowrap" style={{ minWidth: 96 }}>
                      {t('wbAdminReturns.orderColumn')}
                    </th>
                    {visibleColumns.map((col) => (
                      <th key={col} className="text-nowrap">
                        {wbColumnHeader(t, col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const rowId =
                      row.snapshotId != null
                        ? `snap-${row.snapshotId}`
                        : row.srid != null
                          ? `${row.storeSellerId ?? ''}-${String(row.srid)}`
                          : `row-${page}-${idx}`;
                    const expanded = expandedRowId === rowId;
                    return (
                      <React.Fragment key={rowId}>
                        <tr
                          role="button"
                          tabIndex={0}
                          className={`admin-wb-returns-data-row${
                            wbTableRowHighlight ? ' admin-highlight-store-order' : ''
                          }${expanded ? ' table-active' : ''}`}
                          onClick={() => setExpandedRowId(expanded ? null : rowId)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setExpandedRowId(expanded ? null : rowId);
                            }
                          }}
                        >
                          <td
                            className="text-nowrap align-middle"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="py-0 px-1"
                              disabled={
                                row.snapshotId == null ||
                                creatingSnapshotId === row.snapshotId
                              }
                              onClick={() => handleAddToOrder(row.snapshotId)}
                            >
                              {creatingSnapshotId === row.snapshotId ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                t('wbAdminReturns.addToOrder')
                              )}
                            </Button>
                          </td>
                          {visibleColumns.map((col) => (
                            <td
                              key={col}
                              className={
                                col === 'dstOfficeAddress'
                                  ? 'text-wrap-col'
                                  : 'text-nowrap'
                              }
                            >
                              {formatCell(row[col], col, t)}
                            </td>
                          ))}
                        </tr>
                        {expanded && detailColumns.length > 0 ? (
                          <tr className="admin-wb-returns-detail-row">
                            <td
                              colSpan={visibleColumns.length + 1}
                              className="bg-body-secondary border-top py-2 px-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="fw-semibold small mb-2 text-muted">
                                {t('wbAdminReturns.detailFieldsTitle')}
                              </div>
                              <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-2 small">
                                {detailColumns.map((col) => (
                                  <div key={col} className="col">
                                    <span className="text-muted">
                                      {wbColumnHeader(t, col)}:
                                    </span>{' '}
                                    <span className="text-break">
                                      {formatCell(row[col], col, t)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </>
      )}

      {!loading && fetchedOnce && rows.length === 0 && !error && (
        <Alert variant="info">{t('wbAdminReturns.noRows')}</Alert>
      )}
    </div>
  );
};

export default AdminWBStoreReturnsPage;
